/**
 * Phase 2 Defect 2.5 — shared unsaved-changes guard.
 *
 * Two guardrails against silent data loss on any form:
 *   1. `beforeunload` — the browser's native "Leave site? Changes may not
 *      be saved" prompt fires on tab close / refresh / hard-navigation.
 *      Browsers ignore custom message strings for security reasons; only
 *      the boolean returnValue matters.
 *   2. React Router v6 `useBlocker` — intercepts client-side route changes
 *      (<Link> clicks, navigate() calls, back button) and hands us a
 *      blocker object we can use to render a friendly confirmation modal.
 *
 * Both guards activate ONLY when `isDirty === true`, so static / read-only
 * pages that use this hook incur zero overhead.
 *
 * Usage:
 *
 *   const guard = useUnsavedChangesGuard(hasPendingChanges);
 *   return (
 *     <>
 *       <MyForm .../>
 *       <UnsavedChangesModal guard={guard} />
 *     </>
 *   );
 *
 * The returned `guard` exposes:
 *   - blocked:   true when an in-app navigation is being intercepted
 *   - proceed(): let the pending navigation continue (discard changes)
 *   - reset():   cancel the pending navigation and stay on the page
 */
import { useEffect, useRef } from 'react';
import { useBlocker } from 'react-router-dom';

export interface UnsavedChangesGuard {
  /** True when React Router is holding a navigation and waiting for the user to confirm. */
  blocked: boolean;
  /** Let the pending client-side navigation continue (discard unsaved changes). */
  proceed: () => void;
  /** Cancel the pending navigation and stay on the page. */
  reset: () => void;
  /**
   * Synchronously flag the form as saved so the very next navigate() call is
   * allowed through. Fixes the class of bug where mutation.onSuccess flips a
   * `saved` state variable AND calls navigate() in the same tick — React hasn't
   * committed the re-render yet, so useBlocker still sees isDirty=true and
   * pops the "Leave without saving?" modal on a successful save. See
   * memory: [[dreamit2-unsaved-guard-after-save]].
   */
  markSaved: () => void;
}

export function useUnsavedChangesGuard(isDirty: boolean): UnsavedChangesGuard {
  // Set synchronously in onSuccess (before navigate). useBlocker's callback
  // is a fresh closure each render but always deref's savedRef.current, so
  // the mutation → mark-saved → navigate ordering resolves correctly without
  // waiting for a React commit.
  const savedRef = useRef(false);

  // If the parent flips isDirty back to false via normal state (e.g. save +
  // then edits a fresh row), the guard should re-arm — reset savedRef too.
  useEffect(() => {
    if (isDirty) savedRef.current = false;
  }, [isDirty]);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (savedRef.current) return false;
    // Same route (e.g. a search-param tweak) is not a "leave" event.
    if (currentLocation.pathname === nextLocation.pathname) return false;
    return isDirty;
  });

  useEffect(() => {
    if (!isDirty) return;
    function handler(event: BeforeUnloadEvent) {
      if (savedRef.current) return;
      // Modern browsers ignore custom strings; setting returnValue
      // triggers the browser's own generic dialog.
      event.preventDefault();
      event.returnValue = '';
    }
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  return {
    blocked: blocker.state === 'blocked',
    proceed: () => {
      if (blocker.state === 'blocked') blocker.proceed();
    },
    reset: () => {
      if (blocker.state === 'blocked') blocker.reset();
    },
    markSaved: () => {
      savedRef.current = true;
    },
  };
}
